/**
 * @fileoverview TintaRepository - Repositorio especializado para la gestión de tipos de tinta
 */

import { DataRepository } from './base/DataRepository';
import type { 
  Tinta, 
  TintaExpanded,
  CreateTintaData, 
  UpdateTintaData 
} from '../types/collections/Tinta';
import type { ListResult, QueryOptions } from '../types/common/PocketBaseTypes';
import { RepositoryError } from '../types/common/PocketBaseTypes';
import { createModuleLogger } from '../utils/logger';

/**
 * Repositorio especializado para la gestión de tipos de tinta
 * Extiende DataRepository con métodos específicos del dominio
 */
export class TintaRepository extends DataRepository<Tinta> {
  private logger = createModuleLogger('TintaRepository');
  
  constructor() {
    super('tinta');
  }

  /**
   * Obtiene todos los tipos de tinta activos
   * Filtra por estado=true y ordena por nombre
   */
  async getAllActive(): Promise<Tinta[]> {
    try {
      const result = await this.pb.collection(this.collectionName).getFullList<Tinta>({
        filter: 'estado=true',
        sort: 'nombre',
      });
      
      this.logger.debug('Obtuvidos tipos de tinta activos', { count: result.length });
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener tipos de tinta activos: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ALL_ACTIVE_ERROR',
        error
      );
    }
  }

  /**
   * Busca tipos de tinta por nombre
   * Utiliza búsqueda parcial con operador ~
   */
  async searchByName(searchTerm: string): Promise<Tinta[]> {
    try {
      const result = await this.pb.collection(this.collectionName).getFullList<Tinta>({
        filter: `nombre ~ "${searchTerm}"`,
        sort: 'nombre',
      });
      
      this.logger.debug('Búsqueda de tipos de tinta por nombre', { 
        searchTerm, 
        count: result.length 
      });
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al buscar tipos de tinta por nombre "${searchTerm}": ${error instanceof Error ? error.message : String(error)}`,
        'SEARCH_BY_NAME_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene tipos de tinta con paginación y filtros opcionales
   */
  async getListWithFilters(
    page: number = 1,
    pageSize: number = 10,
    filters: {
      search?: string;
      estado?: boolean;
    } = {}
  ): Promise<ListResult<Tinta>> {
    try {
      const filterParts: string[] = [];

      if (filters.search) {
        filterParts.push(`nombre ~ "${filters.search}"`);
      }

      if (filters.estado !== undefined) {
        filterParts.push(`estado=${filters.estado}`);
      }

      const filter = filterParts.length > 0 ? filterParts.join(' && ') : '';

      const result = await this.pb.collection(this.collectionName).getList<Tinta>(
        page,
        pageSize,
        {
          filter,
          sort: 'nombre',
        }
      );

      this.logger.debug('Lista de tipos de tinta con filtros', {
        page,
        pageSize,
        filters,
        totalItems: result.totalItems,
      });

      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener lista de tipos de tinta con filtros: ${error instanceof Error ? error.message : String(error)}`,
        'GET_LIST_WITH_FILTERS_ERROR',
        error
      );
    }
  }

  /**
   * Crea un nuevo tipo de tinta con validaciones
   */
  async createWithValidation(data: CreateTintaData): Promise<Tinta> {
    try {
      // Validaciones de negocio
      if (!data.nombre || data.nombre.trim() === '') {
        throw new Error('El nombre del tipo de tinta es requerido');
      }

      // Verificar que no exista un tipo de tinta con el mismo nombre
      const existingTinta = await this.findFirst(`nombre="${data.nombre.trim()}"`);
      if (existingTinta) {
        throw new Error(`Ya existe un tipo de tinta con el nombre "${data.nombre}"`);
      }

      // Asegurar que se crea como activo por defecto
      const dataToCreate = {
        ...data,
        nombre: data.nombre.trim(),
        estado: data.estado !== undefined ? data.estado : true,
      };

      const result = await this.pb.collection(this.collectionName).create<Tinta>(dataToCreate);
      
      this.logger.info('Tipo de tinta creado exitosamente', { 
        id: result.id, 
        nombre: result.nombre 
      });

      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al crear tipo de tinta: ${error instanceof Error ? error.message : String(error)}`,
        'CREATE_WITH_VALIDATION_ERROR',
        error
      );
    }
  }

  /**
   * Actualiza un tipo de tinta con validaciones
   */
  async updateWithValidation(id: string, data: UpdateTintaData): Promise<Tinta> {
    try {
      // Obtener tipo de tinta actual para validaciones
      const currentTinta = await this.pb.collection(this.collectionName)
        .getOne<Tinta>(id);

      if (!currentTinta) {
        throw new Error(`Tipo de tinta con ID ${id} no encontrado`);
      }

      // Validaciones de negocio
      if (data.nombre !== undefined) {
        if (!data.nombre || data.nombre.trim() === '') {
          throw new Error('El nombre del tipo de tinta no puede estar vacío');
        }

        // Verificar que no exista otro tipo de tinta con el mismo nombre
        const existingTinta = await this.findFirst(
          `nombre="${data.nombre.trim()}" && id!="${id}"`
        );
        if (existingTinta) {
          throw new Error(`Ya existe otro tipo de tinta con el nombre "${data.nombre}"`);
        }
      }

      // Preparar datos para actualización
      const dataToUpdate: UpdateTintaData = { ...data };
      if (data.nombre) {
        dataToUpdate.nombre = data.nombre.trim();
      }

      const result = await this.pb.collection(this.collectionName).update<Tinta>(id, dataToUpdate);
      
      this.logger.info('Tipo de tinta actualizado exitosamente', { 
        id, 
        nombre: result.nombre 
      });

      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al actualizar tipo de tinta ${id}: ${error instanceof Error ? error.message : String(error)}`,
        'UPDATE_WITH_VALIDATION_ERROR',
        error
      );
    }
  }

  /**
   * Desactiva un tipo de tinta (soft delete)
   */
  async deactivate(id: string): Promise<boolean> {
    try {
      await this.pb.collection(this.collectionName).update<Tinta>(id, { 
        estado: false 
      });
      
      this.logger.info('Tipo de tinta desactivado exitosamente', { id });
      return true;
    } catch (error) {
      throw new RepositoryError(
        `Error al desactivar tipo de tinta ${id}: ${error instanceof Error ? error.message : String(error)}`,
        'DEACTIVATE_ERROR',
        error
      );
    }
  }

  /**
   * Activa un tipo de tinta
   */
  async activate(id: string): Promise<boolean> {
    try {
      await this.pb.collection(this.collectionName).update<Tinta>(id, { 
        estado: true 
      });
      
      this.logger.info('Tipo de tinta activado exitosamente', { id });
      return true;
    } catch (error) {
      throw new RepositoryError(
        `Error al activar tipo de tinta ${id}: ${error instanceof Error ? error.message : String(error)}`,
        'ACTIVATE_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene estadísticas básicas de tipos de tinta
   */
  async getStats(): Promise<{
    totalActivos: number;
    totalInactivos: number;
    total: number;
  }> {
    try {
      const [activos, inactivos, total] = await Promise.all([
        this.pb.collection(this.collectionName).getList<Tinta>(1, 1, {
          filter: 'estado=true'
        }),
        this.pb.collection(this.collectionName).getList<Tinta>(1, 1, {
          filter: 'estado=false'
        }),
        this.pb.collection(this.collectionName).getList<Tinta>(1, 1, {})
      ]);

      const stats = {
        totalActivos: activos.totalItems,
        totalInactivos: inactivos.totalItems,
        total: total.totalItems,
      };

      this.logger.debug('Estadísticas de tipos de tinta obtenidas', stats);
      return stats;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener estadísticas de tipos de tinta: ${error instanceof Error ? error.message : String(error)}`,
        'GET_STATS_ERROR',
        error
      );
    }
  }
}
